import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const WORKS = [
  { src: "hats.jpg", caption: "Pink striped hat" },
  { src: "owl-hat.jpg", caption: "Owl hat" },
  { src: "extra-cactus.jpg", caption: "Cactus hat" },
  { src: "ear-warmers.jpg", caption: "Ear warmer set" },
  { src: "extra-reindeer.jpg", caption: "Reindeer ear warmer" },
  { src: "scarves.jpg", caption: "Pink scarf, in the works" },
  { src: "amigurumi.jpg", caption: "Bunny trio" },
  { src: "stuffies.jpg", caption: "Penguin friend" },
  { src: "slippers.jpg", caption: "Cozy slippers, mid-stitch" },
  { src: "baby-blanket.jpg", caption: "Flower baby blanket" },
  { src: "throw-blanket.jpg", caption: "Flower throw blanket" },
  { src: "baby-booties.jpg", caption: "Booties & bear basket" },
  { src: "childrens-cardigans.jpg", caption: "Daisy cardigan" },
  { src: "purses.jpg", caption: "Daisy purse" },
  { src: "coaster-sets.jpg", caption: "Coasters & scrubbies" },
];

export default function Gallery() {
  const [active, setActive] = useState(null);
  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (d) => setActive((a) => (a === null ? null : (a + d + WORKS.length) % WORKS.length)),
    []
  );
  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, step]);
  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow testId="gallery-eyebrow">The Gallery</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-[#2C2520] sm:text-4xl lg:text-5xl">
              Every piece, <span className="italic text-[#A35C45]">made with love.</span>
            </h2>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-[#6E6259]">
            A peek at recent makes — each one stitched by hand, most now living with their new families.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 columns-2 gap-4 sm:columns-3 sm:gap-5 lg:columns-4">
            {WORKS.map((w, i) => (
              <figure
                key={w.src}
                onClick={() => setActive(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setActive(i)}
                aria-label={`View ${w.caption} full screen`}
                data-testid={`gallery-item-${i}`}
                className="group mb-4 cursor-pointer break-inside-avoid overflow-hidden rounded-2xl border border-[#E6DFD5] bg-[#FDFBF7] shadow-sm transition-shadow duration-500 hover:shadow-[0_20px_45px_-18px_rgba(44,37,32,0.25)] sm:mb-5"
              >
                <div className="overflow-hidden">
                  <img
                    src={`/photos/${w.src}`}
                    alt={w.caption}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  />
                </div>
                <figcaption className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C7A6B]">
                  {w.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#2C2520]/92 p-4 backdrop-blur-sm"
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="lightbox-close"
              className="absolute right-5 top-5 rounded-full border border-[#FDFBF7]/25 p-2.5 text-[#FDFBF7]/80 transition-colors duration-300 hover:bg-[#FDFBF7]/10 hover:text-[#FDFBF7]"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photo"
              data-testid="lightbox-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-[#FDFBF7]/25 p-2.5 text-[#FDFBF7]/80 transition-colors duration-300 hover:bg-[#FDFBF7]/10 hover:text-[#FDFBF7] sm:left-6"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl text-center"
            >
              <img
                src={`/photos/${WORKS[active].src}`}
                alt={WORKS[active].caption}
                data-testid="lightbox-image"
                className="mx-auto max-h-[78vh] w-auto rounded-2xl object-contain shadow-2xl"
              />
              <figcaption className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#FDFBF7]/70">
                {WORKS[active].caption} · {active + 1} / {WORKS.length}
              </figcaption>
            </motion.figure>
            <button
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photo"
              data-testid="lightbox-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-[#FDFBF7]/25 p-2.5 text-[#FDFBF7]/80 transition-colors duration-300 hover:bg-[#FDFBF7]/10 hover:text-[#FDFBF7] sm:right-6"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
